/**
 * Import a booking — Tripsby Together (P4).
 *
 * Host pastes a forwarded hotel confirmation (email body / .eml text) and/or
 * drops a screenshot/PDF. The concierge-builder API extracts the reservation
 * details (extract-only — nothing is committed), the host reviews/edits them,
 * picks the room block and confirms the guest, then confirms. On confirm this
 * builds an AssignReservationRequest with status "booked" and hands it to the
 * existing host-assign path (groupsApi.assignReservation), which only SAVES the
 * reservation. The host sends the guest's stay confirmation explicitly from the
 * Requests list. No new email template is involved.
 */
import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AutoAwesome, CloudUpload } from '@mui/icons-material';
import { conciergeApi } from '../../services/concierge-api-client';
import type {
  AssignReservationRequest,
  BookingExtractionData,
  GroupInvitee,
  GroupRoomBlock,
} from '../../types/groups';
import {
  buildAssignReservationBody,
  matchInviteeByEmail,
  suggestRoomBlock,
  validateBookingAssign,
} from '../../lib/booking-import';

const EXTRACTED_GUEST = '__extracted__';
const ACCEPTED_FILE_TYPES = '.jpg,.jpeg,.png,.webp,.pdf,.eml,.txt';

function isTextLike(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    file.type.startsWith('text/') ||
    file.type === 'message/rfc822' ||
    name.endsWith('.eml') ||
    name.endsWith('.txt')
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function confidenceColor(score: number): 'success' | 'warning' | 'error' {
  if (score >= 0.75) return 'success';
  if (score >= 0.5) return 'warning';
  return 'error';
}

export default function BookingImportDialog({
  invitees,
  roomBlocks,
  saving,
  onClose,
  onAssign,
}: {
  invitees: GroupInvitee[];
  roomBlocks: GroupRoomBlock[];
  saving: boolean;
  onClose: () => void;
  onAssign: (body: AssignReservationRequest) => void;
}) {
  // --- input step ---
  const [emailText, setEmailText] = useState('');
  const [fileB64, setFileB64] = useState('');
  const [fileMime, setFileMime] = useState('');
  const [fileName, setFileName] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- preview step (populated after extraction) ---
  const [extracted, setExtracted] = useState<BookingExtractionData | null>(null);
  const [confidence, setConfidence] = useState(0);
  const [hotelName, setHotelName] = useState('');
  const [roomType, setRoomType] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [rateAmount, setRateAmount] = useState('');
  const [rateCurrency, setRateCurrency] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [roomBlockId, setRoomBlockId] = useState('');
  const [guestSelection, setGuestSelection] = useState('');

  const handleFile = async (file: File) => {
    setError('');
    if (isTextLike(file)) {
      try {
        const text = await file.text();
        setEmailText((prev) => (prev.trim() ? `${prev}\n\n${text}` : text));
        setFileB64('');
        setFileMime('');
        setFileName('');
      } catch {
        setError('Could not read that file. Paste the confirmation text instead.');
      }
      return;
    }
    if (file.type.startsWith('image/') || file.type === 'application/pdf') {
      try {
        setFileB64(await fileToBase64(file));
        setFileMime(file.type);
        setFileName(file.name);
      } catch {
        setError('Could not read that file. Try another, or paste the text.');
      }
      return;
    }
    setError('Upload an image, PDF, or .eml/.txt file — or paste the confirmation text.');
  };

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const seedPreview = (data: BookingExtractionData) => {
    setExtracted(data);
    setConfidence(data.confidence_score ?? 0);
    setHotelName(data.hotel_name || '');
    setRoomType(data.room_type || '');
    setConfirmation(data.confirmation_number || '');
    setArrivalDate(data.arrival_date || '');
    setDepartureDate(data.departure_date || '');
    setRateAmount(data.rate_amount != null ? String(data.rate_amount) : '');
    setRateCurrency(data.rate_currency || '');
    setGuestName(data.guest_name || '');
    setGuestEmail(data.guest_email || '');
    setGuestPhone(data.guest_phone || '');

    const block = suggestRoomBlock(data.hotel_name, roomBlocks);
    // Only pre-select when the extracted hotel actually matches a block — never
    // default to "the first block", which silently picks a plausible-but-wrong one.
    setRoomBlockId(block?.id || '');

    const matched = matchInviteeByEmail(data.guest_email, invitees);
    setGuestSelection(matched?.id || (data.guest_name ? EXTRACTED_GUEST : ''));
  };

  const handleExtract = async () => {
    if (!emailText.trim() && !fileB64) {
      setError('Paste the confirmation text or attach a screenshot/PDF first.');
      return;
    }
    setError('');
    setExtracting(true);
    try {
      const res = await conciergeApi.importBooking({
        email_text: emailText.trim() || undefined,
        file_base64: fileB64 || undefined,
        mime_type: fileMime || undefined,
      });
      seedPreview(res.booking_data);
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  const usingExtractedGuest = guestSelection === EXTRACTED_GUEST;
  const selectedInvitee = useMemo(
    () => invitees.find((i) => i.id === guestSelection),
    [invitees, guestSelection],
  );

  const assignInput = useMemo(
    () => ({
      roomBlockId,
      inviteeId: usingExtractedGuest ? undefined : guestSelection || undefined,
      guestName: usingExtractedGuest ? guestName : undefined,
      guestEmail: usingExtractedGuest ? guestEmail : undefined,
      guestPhone: usingExtractedGuest ? guestPhone : undefined,
      confirmationNumber: confirmation,
      arrivalDate: arrivalDate || undefined,
      departureDate: departureDate || undefined,
      rateAmount,
      rateCurrency,
      roomType,
    }),
    [
      roomBlockId,
      usingExtractedGuest,
      guestSelection,
      guestName,
      guestEmail,
      guestPhone,
      confirmation,
      arrivalDate,
      departureDate,
      rateAmount,
      rateCurrency,
      roomType,
    ],
  );

  const errors = useMemo(() => validateBookingAssign(assignInput), [assignInput]);
  const resolvedEmail = usingExtractedGuest ? guestEmail.trim() : selectedInvitee?.email || '';
  const noEmailForGuest = !resolvedEmail;

  const handleConfirm = () => {
    if (errors.length) return;
    onAssign(buildAssignReservationBody(assignInput));
  };

  // ---------- render: input step ----------
  if (!extracted) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Import a booking</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Paste a forwarded hotel confirmation, or drop a screenshot/PDF. We'll read the
              details with AI for you to review — nothing is sent until you confirm.
            </Typography>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {roomBlocks.length === 0 && (
              <Alert severity="warning">
                Add a room block first — a reservation needs a hotel to attach to.
              </Alert>
            )}
            <TextField
              label="Confirmation email / text"
              placeholder="Paste the forwarded booking confirmation here…"
              value={emailText}
              onChange={(e) => setEmailText(e.target.value)}
              multiline
              minRows={6}
              fullWidth
            />
            <Paper
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              variant="outlined"
              sx={{
                p: 3,
                border: 2,
                borderStyle: 'dashed',
                borderColor: 'grey.300',
                borderRadius: 3,
                cursor: 'pointer',
                textAlign: 'center',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <CloudUpload sx={{ fontSize: 30, color: 'text.secondary', mb: 0.5 }} />
              <Typography variant="body2" fontWeight={500}>
                {fileName || 'Drop a confirmation screenshot, PDF, or .eml'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                JPG, PNG, PDF, or .eml/.txt
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept={ACCEPTED_FILE_TYPES}
                onChange={onFileSelect}
              />
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={extracting ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
            disabled={extracting || (!emailText.trim() && !fileB64)}
            onClick={handleExtract}
          >
            {extracting ? 'Reading…' : 'Extract details'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ---------- render: preview / confirm step ----------
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <span>Review the imported booking</span>
          <Chip
            size="small"
            color={confidenceColor(confidence)}
            label={`${Math.round(confidence * 100)}% confident`}
          />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {confidence < 0.5 && (
            <Alert severity="warning">
              Low confidence — double-check every field below before confirming.
            </Alert>
          )}
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              label="Hotel (from confirmation)"
              value={hotelName}
              onChange={(e) => setHotelName(e.target.value)}
              helperText="Informational — the room block below sets the hotel on the email."
            />
            <TextField
              fullWidth
              label="Room type (from confirmation)"
              value={roomType}
              onChange={(e) => setRoomType(e.target.value)}
            />
          </Stack>

          <TextField
            select
            fullWidth
            label="Room block"
            value={roomBlockId}
            onChange={(e) => setRoomBlockId(e.target.value)}
            error={!roomBlockId}
            helperText={
              roomBlockId ? 'The reservation attaches to this room block.' : 'Pick a room block.'
            }
          >
            {roomBlocks.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.hotelName} · {b.roomType} ({b.availableRooms} left)
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Guest"
            value={guestSelection}
            onChange={(e) => setGuestSelection(e.target.value)}
            error={!guestSelection}
            helperText={
              usingExtractedGuest
                ? 'Not in your invitee list — the reservation records the extracted guest.'
                : 'Matched to an existing invitee.'
            }
          >
            {invitees.map((i) => (
              <MenuItem key={i.id} value={i.id}>
                {i.fullName}
                {i.email ? ` · ${i.email}` : ''}
              </MenuItem>
            ))}
            {guestName ? (
              <MenuItem value={EXTRACTED_GUEST}>Add "{guestName}" as a new guest</MenuItem>
            ) : null}
          </TextField>

          {usingExtractedGuest && (
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Guest name"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
              />
              <TextField
                fullWidth
                label="Guest email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
              />
              <TextField
                label="Phone"
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                sx={{ width: 150 }}
              />
            </Stack>
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              label="Check-in"
              type="date"
              value={arrivalDate}
              onChange={(e) => setArrivalDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Check-out"
              type="date"
              value={departureDate}
              onChange={(e) => setDepartureDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              label="Confirmation number"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
            <TextField
              label="Rate (per night)"
              type="number"
              value={rateAmount}
              onChange={(e) => setRateAmount(e.target.value)}
              sx={{ width: 160 }}
            />
            <TextField
              label="Currency"
              value={rateCurrency}
              onChange={(e) => setRateCurrency(e.target.value)}
              inputProps={{ maxLength: 3 }}
              sx={{ width: 110 }}
            />
          </Stack>

          {errors.length > 0 && (
            <Alert severity="error">
              <Stack>
                {errors.map((msg) => (
                  <span key={msg}>{msg}</span>
                ))}
              </Stack>
            </Alert>
          )}
          {noEmailForGuest && errors.length === 0 && (
            <Alert severity="warning">
              This guest has no email — you won't be able to send them a stay confirmation.
            </Alert>
          )}
          {!noEmailForGuest && errors.length === 0 && (
            <Alert severity="info">
              Saved to the reservation — send the guest their confirmation from the Requests list when you're ready.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setExtracted(null)} disabled={saving}>
          Back
        </Button>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || errors.length > 0 || !roomBlockId || !guestSelection}
          onClick={handleConfirm}
        >
          {saving ? 'Saving…' : 'Import & save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
